import type { RecipeDef, StageDef } from "@reagent/sim";
import { freshCardInstances } from "@reagent/sim";
import { ALL_CARDS } from "../../cards";
import { pollutionCardsFor, reachablePollutionReasons } from "../../pollution";
import { setupOfferFor } from "../../schema";
import type { StageSolution } from "../../solutions";
import { ERA_1_SCALARS } from "./shared";

/**
 * Greek Fire (GF), spec 0037 T3 (docs/design/06-eras-and-stages.md 4.3/4.6).
 *
 * Core: crude naphtha is heated, flashed in a pre-placed phase converter, and
 * its two fractions are routed apart: the light naphtha vapour goes on to the
 * blend reactor, the Fuel liquid (with the Budget crude's Sludge) is the pathway's
 * export. Refined: Greek Fire and Brine react to Sea Fire, and the product is
 * pumped into a siphon buffer.
 *
 * Every number below is a PLACEHOLDER (spec 0037): chosen so the worked solutions
 * clear, marked, and listed in the worklog as needing playtest.
 */

/**
 * PLACEHOLDER (spec 0037): needs playtest. The flash point sits above ambient (20)
 * and below what one heater pass (+30) delivers, so an unheated crude never flashes.
 * The recipe conserves composition (schema rule), so it names both the volatile
 * LightNaphtha and the Fuel the liquid route exports.
 */
export const GF_STILL_FLASH: RecipeDef = {
  id: "gf-still-flash",
  inputs: { LightNaphtha: 1, Fuel: 1 },
  outputs: { LightNaphtha: 1, Fuel: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["phaseConverter"],
  boilingPoint: 40,
  volatileSpecies: ["LightNaphtha"],
};

/**
 * PLACEHOLDER (spec 0037): needs playtest. `a` = 2 naphtha per Lime. The yield is high
 * on purpose: every Budget Lime packet carries Sludge into the product packet, and an
 * infested (half-speed) blend pools a second Lime packet's worth before it fires. Purity
 * is the dominant species' share, so the Greek Fire has to outweigh all of that at a
 * doubled market grade and beyond. Heat 2 per firing is what makes the blend need a jacket.
 */
export const GF_BLEND_FIRE: RecipeDef = {
  id: "gf-blend-fire",
  inputs: { LightNaphtha: 2, Lime: 1 },
  outputs: { GreekFire: 24 },
  isExothermic: true,
  heat: 2,
  nodeKinds: ["reactor"],
};

/**
 * PLACEHOLDER (spec 0037): needs playtest. Both feeds arrive impure, so as above the
 * yield outweighs two packets' Sludge. Heat 1 per firing, one firing a tick: a single
 * jacket (shed 1) holds it, no jacket runs away.
 */
export const GF_SIPHON_SEA: RecipeDef = {
  id: "gf-siphon-sea",
  inputs: { GreekFire: 1, Brine: 1 },
  outputs: { SeaFire: 24 },
  isExothermic: true,
  heat: 1,
  nodeKinds: ["reactor"],
};

export const GF_RECIPES: readonly RecipeDef[] = [GF_STILL_FLASH, GF_BLEND_FIRE, GF_SIPHON_SEA];

const cardById = (id: string) => {
  const card = ALL_CARDS.find((c) => c.id === id);
  if (!card) throw new Error(`gf: unknown card ${id}`);
  return card;
};

const path = (points: readonly (readonly [number, number])[]) => points.map(([x, y]) => ({ x, y }));

/** Contiguous straight run along row `y` from `x0` to `x1` inclusive. */
const row = (y: number, x0: number, x1: number): [number, number][] =>
  Array.from({ length: x1 - x0 + 1 }, (_, i): [number, number] => [x0 + i, y]);

/*
 * PLACEHOLDER (spec 0037): needs playtest. Setup ₵ for each module: rig plus both
 * mandatory contracts, with room for a doubled market on both (spec 0036), which is
 * the most the contracts can cost.
 */
const CORE_SETUP_BUDGET_CREDITS = 50;
const REFINED_SETUP_BUDGET_CREDITS = 70;

const scalars = {
  energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
  handSize: ERA_1_SCALARS.handSize,
  ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
  maxTurns: ERA_1_SCALARS.maxTurns,
  liveBuild: ERA_1_SCALARS.liveBuild,
} as const;

function finish(stage: StageDef): StageDef {
  // Mapped from reachability, like testing.ts's refinedModule: validateStage demands every reachable reason.
  return { ...stage, pollutionCards: pollutionCardsFor(...reachablePollutionReasons(stage, ALL_CARDS)) };
}

/*
 * PLACEHOLDER (spec 0037): needs playtest. Contract prices and impurity units:
 * Premium is clean and dearer than Budget; Budget carries Sludge by convention.
 */
const CRUDE_SOURCING = {
  budget: { impurities: { Sludge: 1 }, costCredits: 6 },
  premium: { costCredits: 14 },
} as const;
const LIME_SOURCING = {
  budget: { impurities: { Sludge: 1 }, costCredits: 4 },
  premium: { costCredits: 10 },
} as const;
const FIRE_SOURCING = CRUDE_SOURCING;
const BRINE_SOURCING = LIME_SOURCING;

/**
 * Core: the still. The heater is bought in Setup and piped in by the player; the
 * converter, the blend reactor and both role edges are pre-placed.
 */
export const GF_CORE: StageDef = finish({
  id: "era1-gf-core",
  name: "Greek Fire",
  blurb: "Flash the crude, send each fraction its own way, and keep the vapour from building pressure.",
  cols: 16,
  rows: 5,
  nodes: [
    // Crude naphtha: light naphtha to flash off, Fuel to sell, both riding on Sludge when bought Budget. Fuel is the
    // largest species on purpose: a residence-sludged packet gives up a Fuel unit, never the naphtha the blend needs.
    {
      id: "gf-crude",
      kind: "inputTank",
      pos: { x: 0, y: 3 },
      emits: { Fuel: 3, LightNaphtha: 2 },
      sourcing: CRUDE_SOURCING,
    },
    { id: "gf-lime", kind: "inputTank", pos: { x: 0, y: 1 }, emits: { Lime: 1 }, sourcing: LIME_SOURCING },
    // PLACEHOLDER (spec 0037): a converter that holds only two packets takes one crude packet per two ticks. It
    // emits two fractions per packet but pushes one a tick, and a roomier buffer lets a hot raw packet
    // slip out unconverted (phase.ts flashes the oldest hot packet, movement.ts pushes the oldest ready one).
    { id: "gf-still", kind: "phaseConverter", pos: { x: 5, y: 3 }, bufferCapacity: 2, recipeIds: ["gf-still-flash"] },
    { id: "gf-blend", kind: "reactor", pos: { x: 8, y: 2 }, recipeIds: ["gf-blend-fire"] },
    { id: "gf-fire-out", kind: "productHopper", pos: { x: 15, y: 2 }, bufferCapacity: 99 },
    { id: "gf-fuel-out", kind: "wasteBuyer", pos: { x: 15, y: 4 } },
  ],
  edges: [
    { id: "gf-c-lime-feed", fromNodeId: "gf-lime", toNodeId: "gf-blend", path: path([...row(1, 0, 8), [8, 2]]) },
    {
      id: "gf-c-vapour",
      fromNodeId: "gf-still",
      toNodeId: "gf-blend",
      phaseRole: "vapour",
      path: path([...row(3, 5, 8), [8, 2]]),
    },
    {
      id: "gf-c-liquid",
      fromNodeId: "gf-still",
      toNodeId: "gf-fuel-out",
      phaseRole: "liquid",
      path: path([[5, 3], ...row(4, 5, 15)]),
    },
    { id: "gf-c-product", fromNodeId: "gf-blend", toNodeId: "gf-fire-out", path: path(row(2, 8, 15)) },
  ],
  recipes: [GF_STILL_FLASH, GF_BLEND_FIRE],
  objective: {
    // PLACEHOLDER (spec 0037): needs playtest. Three firings; an unjacketed blend explodes before its first product lands.
    targetComposition: { GreekFire: 72 },
    minPurity: ERA_1_SCALARS.minPurity,
  },
  ...scalars,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances([
    "card-cooling-jacket",
    "card-cooling-jacket",
    "card-burst-disk",
    "card-nitrogen-purge",
    "card-pipe-glass",
    "card-solvent-flush",
    "card-rush-order",
  ]),
  setupBudgetCredits: CORE_SETUP_BUDGET_CREDITS,
  setupCatalogue: [
    setupOfferFor(cardById("card-gf-blend")),
    setupOfferFor(cardById("card-mixer")),
    setupOfferFor(cardById("card-mixer-heavy")),
    setupOfferFor(cardById("card-heater")),
    setupOfferFor(cardById("card-pipe-glass")),
  ],
});

export const GF_CORE_SOLUTION: StageSolution = {
  stageId: "era1-gf-core",
  sourcing: { "gf-crude": "budget", "gf-lime": "budget" },
  setupBuild: [
    { cardId: "card-heater", at: { x: 2, y: 3 } }, // n1
    { cardId: "card-pipe-glass", fromNodeId: "gf-crude", toNodeId: "n1" },
    { cardId: "card-pipe-glass", fromNodeId: "n1", toNodeId: "gf-still" },
  ],
  turns: [
    [
      { cardId: "card-cooling-jacket", at: { x: 8, y: 2 } },
      // Both pipes into the flash stall on alternate ticks while the converter clears one crude packet per two; a
      // disk on each keeps a bad run of stalls from bursting them (03-hazards 2.2).
      { cardId: "card-burst-disk", at: { x: 2, y: 3 } },
      { cardId: "card-burst-disk", at: { x: 5, y: 3 } },
    ],
  ],
  notes:
    "The still needs heat before it can split anything: buy the heater between the crude tank and the converter " +
    "and pipe it in. The pre-placed converter then sends the naphtha vapour to the blend and the Fuel liquid to the " +
    "export. The blend is exothermic and fires every other tick, so jacket it (03-hazards 2.1), and put a burst disk on each pipe that feeds the flash.",
};

/** Refined: Sea Fire, pumped into a siphon buffer. The pump, the buffer and their pipes are bought in Setup. */
export const GF_REFINED: StageDef = finish({
  id: "era1-gf-refined",
  name: "Sea Fire",
  blurb: "Greek Fire and brine, pumped into a siphon before the reactor can run away.",
  cols: 16,
  rows: 5,
  nodes: [
    { id: "gf-fire-tank", kind: "inputTank", pos: { x: 0, y: 1 }, emits: { GreekFire: 1 }, sourcing: FIRE_SOURCING },
    { id: "gf-brine", kind: "inputTank", pos: { x: 0, y: 3 }, emits: { Brine: 1 }, sourcing: BRINE_SOURCING },
    { id: "gf-siphon", kind: "reactor", pos: { x: 6, y: 2 }, recipeIds: ["gf-siphon-sea"] },
    { id: "gf-sea-out", kind: "productHopper", pos: { x: 15, y: 2 }, bufferCapacity: 99 },
  ],
  edges: [
    { id: "gf-r-fire-feed", fromNodeId: "gf-fire-tank", toNodeId: "gf-siphon", path: path([...row(1, 0, 6), [6, 2]]) },
    { id: "gf-r-brine-feed", fromNodeId: "gf-brine", toNodeId: "gf-siphon", path: path([...row(3, 0, 6), [6, 2]]) },
  ],
  recipes: [GF_SIPHON_SEA],
  objective: {
    // PLACEHOLDER (spec 0037): needs playtest. Four firings; an unjacketed reactor explodes before its first product lands.
    targetComposition: { SeaFire: 96 },
    minPurity: ERA_1_SCALARS.minPurity,
  },
  ...scalars,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances([
    "card-cooling-jacket",
    "card-cooling-jacket",
    "card-burst-disk",
    "card-nitrogen-purge",
    "card-pipe-glass",
    "card-solvent-flush",
    "card-rush-order",
  ]),
  setupBudgetCredits: REFINED_SETUP_BUDGET_CREDITS,
  setupCatalogue: [
    setupOfferFor(cardById("card-gf-siphon")),
    setupOfferFor(cardById("card-mixer")),
    setupOfferFor(cardById("card-mixer-heavy")),
    setupOfferFor(cardById("card-pump")),
    setupOfferFor(cardById("card-storage-tank")),
    setupOfferFor(cardById("card-pipe-glass")),
  ],
});

export const GF_REFINED_SOLUTION: StageSolution = {
  stageId: "era1-gf-refined",
  sourcing: { "gf-fire-tank": "budget", "gf-brine": "budget" },
  setupBuild: [
    { cardId: "card-pump", at: { x: 9, y: 2 } }, // n1
    { cardId: "card-storage-tank", at: { x: 12, y: 2 } }, // n2
    { cardId: "card-pipe-glass", fromNodeId: "gf-siphon", toNodeId: "n1" },
    { cardId: "card-pipe-glass", fromNodeId: "n1", toNodeId: "n2" },
    { cardId: "card-pipe-glass", fromNodeId: "n2", toNodeId: "gf-sea-out" },
  ],
  turns: [[{ cardId: "card-cooling-jacket", at: { x: 6, y: 2 } }]],
  notes:
    "The reactor has no way out until you build one: a pump at its mouth drives the product into the storage tank " +
    "that acts as the siphon buffer, and that empties into the hopper. The reaction fires every tick, so jacket it.",
};
